/**
 * NetworkCombobox — searchable picker over the full mobile-network catalogue.
 *
 * The `networks` Firestore collection is seeded with the ~2.5k-entry MCC-MNC
 * database (see scripts/generate-mcc-mnc-data.mjs +
 * apps/frontend-new/scripts/seed-networks-from-mcc-mnc.ts), so a plain <select>
 * is no longer usable. This combobox loads the catalogue once (cached in
 * esimService) and fuzzy-matches client-side on operator name, country, ISO,
 * MCC, MNC and the combined MCC+MNC PLMN. The org's custom networks surface
 * first.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Globe, Plus } from 'lucide-react';
import type { Network } from '@simcheck/shared-types';
import { lookupOperatorName, looksLikeNumericPlmn } from '@simcheck/shared-types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { createCustomNetwork, loadNetworkCatalog } from '@/services/esimService';

interface NetworkComboboxProps {
  organizationId: string;
  /** Currently selected network id. */
  value: string;
  /** The selected network, so we can render its label before the full
   *  catalogue has loaded (e.g. editing an existing profile). Only needs
   *  id/name; country is optional (the profile may not carry it). */
  selected?: (Pick<Network, 'id' | 'name'> & { country?: string }) | null;
  onChange: (network: Network) => void;
  disabled?: boolean;
  placeholder?: string;
  /** The acting user's uid — required to attribute a created network
   *  (createCustomNetwork). */
  userId?: string;
  /** Enables the inline "Add a network manually" affordance and sets how the
   *  scope of a created network is decided:
   *   - `'org'` — always scoped to `organizationId`, no choice shown
   *     (org owners/admins)
   *   - `'choose'` — the create form shows a System-wide / This-organization
   *     toggle so the user picks per network (super admins)
   *  Omit to hide the affordance. Requires `userId`. */
  createScope?: 'org' | 'choose';
}

const MAX_RESULTS = 60;

const inputCls =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function plmn(n: Network): string {
  return `${n.mcc ?? ''}${n.mnc ?? ''}`;
}

/** Score a network against the lowercased query. Lower = better; -1 = no match. */
function scoreNetwork(n: Network, q: string): number {
  const name = (n.name ?? '').toLowerCase();
  const country = (n.country ?? '').toLowerCase();
  const code = (n.countryCode ?? '').toLowerCase();
  const mcc = (n.mcc ?? '').toLowerCase();
  const mnc = (n.mnc ?? '').toLowerCase();
  const combined = plmn(n).toLowerCase();

  if (name === q || combined === q) return 0;
  if (name.startsWith(q)) return 1;
  if (mcc === q || combined.startsWith(q)) return 2;
  if (country.startsWith(q) || code === q) return 3;
  if (name.includes(q)) return 4;
  if (country.includes(q)) return 5;
  if (mnc === q) return 6;
  return -1;
}

const emptyForm = { name: '', mcc: '', mnc: '', country: '', countryCode: '' };

export function NetworkCombobox({
  organizationId,
  value,
  selected,
  onChange,
  disabled,
  placeholder = 'Search networks by name, country, or MCC/MNC…',
  userId,
  createScope,
}: NetworkComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<Network[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedFor = useRef<string | null>(null);

  // Inline "add a network manually" sub-view state.
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  // Only consulted when createScope === 'choose' (super admins). Defaults to
  // system-wide — the usual super-admin action is filling a catalogue gap.
  const [scopeChoice, setScopeChoice] = useState<'global' | 'org'>('global');

  // Lazily load the catalogue the first time the popover opens for this org.
  useEffect(() => {
    if (!open || loadedFor.current === organizationId) return;
    let cancelled = false;
    // Org changed since the last load — drop the previous org's catalogue so
    // its custom networks don't flash before the new load resolves.
    if (loadedFor.current && loadedFor.current !== organizationId) {
      setCatalog(null);
    }
    setLoading(true);
    setError(null);
    loadNetworkCatalog(organizationId)
      .then((nets) => {
        if (cancelled) return;
        setCatalog(nets);
        loadedFor.current = organizationId;
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load networks');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, organizationId]);

  const selectedNetwork = useMemo(
    () => selected ?? catalog?.find((n) => n.id === value) ?? null,
    [selected, catalog, value]
  );

  const { custom, global, total } = useMemo(() => {
    const all = catalog ?? [];
    const q = query.trim().toLowerCase();

    let matches: Network[];
    if (!q) {
      // No query: show custom networks + a slice of the catalogue alphabetically.
      matches = all;
    } else {
      matches = all
        .map((n) => ({ n, s: scoreNetwork(n, q) }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => a.s - b.s || a.n.name.localeCompare(b.n.name))
        .map((x) => x.n);
    }

    const customMatches = matches.filter((n) => !n.isGlobal).slice(0, MAX_RESULTS);
    const remaining = Math.max(0, MAX_RESULTS - customMatches.length);
    const globalMatches = matches.filter((n) => n.isGlobal).slice(0, remaining);
    return { custom: customMatches, global: globalMatches, total: matches.length };
  }, [catalog, query]);

  const shownCount = custom.length + global.length;
  const hiddenCount = Math.max(0, total - shownCount);

  const handleSelect = (network: Network) => {
    onChange(network);
    setOpen(false);
    setQuery('');
  };

  const trimmedQuery = query.trim();
  const queryIsPlmn = looksLikeNumericPlmn(trimmedQuery);
  const canCreate = !!createScope && !!userId;
  // 'org' scope is always org-local; 'choose' follows the toggle.
  const effectiveGlobal = createScope === 'choose' && scopeChoice === 'global';

  // Open the create sub-view, prefilling from whatever the user typed. A
  // numeric query (e.g. "311960") splits into MCC (first 3) + MNC (rest) and
  // borrows the country from any catalogue sibling on the same MCC; free text
  // seeds the operator name.
  const openCreateForm = () => {
    let next = { ...emptyForm };
    if (queryIsPlmn) {
      const mcc = trimmedQuery.slice(0, 3);
      const mnc = trimmedQuery.slice(3);
      const sibling = (catalog ?? []).find((n) => n.mcc === mcc);
      next = {
        name: lookupOperatorName(trimmedQuery) ?? '',
        mcc,
        mnc,
        country: sibling?.country ?? '',
        countryCode: sibling?.countryCode ?? '',
      };
    } else {
      next.name = trimmedQuery;
    }
    setForm(next);
    setScopeChoice('global');
    setSaveError(null);
    setCreating(true);
  };

  // Editing MCC re-derives the country from a catalogue sibling, but only when
  // the country field is still empty so we never clobber a manual entry.
  const handleMccChange = (raw: string) => {
    const mcc = raw.replace(/\D/g, '').slice(0, 3);
    setForm((f) => {
      let { country, countryCode } = f;
      if (mcc.length === 3 && !country.trim()) {
        const sibling = (catalog ?? []).find((n) => n.mcc === mcc);
        if (sibling) {
          country = sibling.country ?? '';
          countryCode = sibling.countryCode ?? '';
        }
      }
      return { ...f, mcc, country, countryCode };
    });
  };

  const handleCreateNetwork = async () => {
    if (!userId) return;
    const name = form.name.trim();
    const mcc = form.mcc.trim();
    const mnc = form.mnc.trim();
    if (!name) return setSaveError('Operator name is required.');
    if (!/^\d{3}$/.test(mcc)) return setSaveError('MCC must be 3 digits.');
    if (!/^\d{2,3}$/.test(mnc)) return setSaveError('MNC must be 2–3 digits.');

    setSaving(true);
    setSaveError(null);
    try {
      const network = await createCustomNetwork(organizationId, userId, {
        name,
        mcc,
        mnc,
        country: form.country.trim(),
        countryCode: form.countryCode.trim().toUpperCase(),
        isGlobal: effectiveGlobal,
      });
      // Surface it immediately without a full catalogue reload (custom networks
      // aren't cached; globals are).
      setCatalog((prev) => (prev ? [network, ...prev] : [network]));
      onChange(network);
      setCreating(false);
      setOpen(false);
      setQuery('');
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to add network.');
    } finally {
      setSaving(false);
    }
  };

  const renderItem = (n: Network) => (
    <CommandItem key={n.id} value={n.id} onSelect={() => handleSelect(n)}>
      <Check
        className={cn('h-4 w-4', value === n.id ? 'opacity-100' : 'opacity-0')}
      />
      <span className="truncate">{n.name}</span>
      <span className="ml-auto pl-2 text-xs text-muted-foreground whitespace-nowrap">
        {n.country} · {plmn(n)}
      </span>
    </CommandItem>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setCreating(false);
          setSaveError(null);
          setQuery('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selectedNetwork ? (
            <span className="flex min-w-0 items-center gap-2">
              <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedNetwork.name}
                {selectedNetwork.country ? (
                  <span className="text-muted-foreground"> · {selectedNetwork.country}</span>
                ) : null}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Select a network</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {creating ? (
          <div className="space-y-3 p-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Plus className="h-4 w-4" />
              Add a custom network
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Operator name *</label>
              <input
                className={inputCls}
                placeholder="e.g. Lyca Mobile"
                value={form.name}
                autoFocus
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">MCC *</label>
                <input
                  className={inputCls}
                  placeholder="311"
                  inputMode="numeric"
                  value={form.mcc}
                  onChange={(e) => handleMccChange(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">MNC *</label>
                <input
                  className={inputCls}
                  placeholder="960"
                  inputMode="numeric"
                  value={form.mnc}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, mnc: e.target.value.replace(/\D/g, '').slice(0, 3) }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-[1fr_5rem] gap-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Country</label>
                <input
                  className={inputCls}
                  placeholder="United States"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">ISO</label>
                <input
                  className={cn(inputCls, 'uppercase')}
                  placeholder="US"
                  maxLength={2}
                  value={form.countryCode}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      countryCode: e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 2),
                    }))
                  }
                />
              </div>
            </div>
            {createScope === 'choose' && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Visibility</label>
                <div className="grid grid-cols-2 gap-1 rounded-md border border-input p-1">
                  <button
                    type="button"
                    onClick={() => setScopeChoice('global')}
                    className={cn(
                      'rounded px-2 py-1.5 text-xs font-medium transition-colors',
                      scopeChoice === 'global'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    System-wide
                  </button>
                  <button
                    type="button"
                    onClick={() => setScopeChoice('org')}
                    className={cn(
                      'rounded px-2 py-1.5 text-xs font-medium transition-colors',
                      scopeChoice === 'org'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    This organization
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {effectiveGlobal
                ? 'Added to the shared catalogue — available to all organizations.'
                : 'Scoped to your organization only.'}
            </p>
            {saveError && <p className="text-xs text-destructive">{saveError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => {
                  setCreating(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" disabled={saving} onClick={handleCreateNetwork}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Add network
              </Button>
            </div>
          </div>
        ) : (
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading networks…
              </div>
            )}
            {error && !loading && (
              <div className="py-6 text-center text-sm text-destructive">{error}</div>
            )}
            {!loading && !error && shownCount === 0 && (
              <CommandEmpty>No networks found.</CommandEmpty>
            )}
            {!loading && !error && custom.length > 0 && (
              <CommandGroup heading="Your networks">{custom.map(renderItem)}</CommandGroup>
            )}
            {!loading && !error && global.length > 0 && (
              <CommandGroup heading={query.trim() ? 'Matches' : 'All networks'}>
                {global.map(renderItem)}
              </CommandGroup>
            )}
            {!loading && !error && hiddenCount > 0 && (
              <div className="px-3 py-2 text-center text-xs text-muted-foreground">
                +{hiddenCount.toLocaleString()} more — keep typing to narrow down
              </div>
            )}
            {!loading && !error && canCreate && (
              <CommandGroup heading="Not listed?">
                <CommandItem value="__add_custom_network__" onSelect={openCreateForm}>
                  <Plus className="h-4 w-4" />
                  <span>
                    {queryIsPlmn
                      ? `Add MCC/MNC ${trimmedQuery} manually`
                      : 'Add a network manually'}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NetworkCombobox;
